import { useState } from "react";
import { useTranslate } from "@refinedev/core";
import { DateField, SelectField } from "@fawkes/ui";

/**
 * Het periodefilter van de financiële overzichten: deze maand, dit kwartaal, dit jaar, een eigen
 * periode, of alles.
 *
 * <p><b>Waarom er een eigen periode bij hoort.</b> De vaste keuzes beantwoorden alleen vragen die
 * toevallig op een maand- of jaargrens vallen. "Wat is er in de zomervakantie omgegaan" of "laat de
 * facturen van vorige maand zien" kon niet, en viel dus terug op Alles plus zelf zoeken (Maarten,
 * 2026-10-23).
 *
 * <p>De eigen periode staat vóór Alles in de lijst: hij hoort bij de andere periodes, en Alles is
 * het ontbreken van een periode.
 */

export type Periode = string;

/** Een eigen periode: twee ISO-datums, allebei mogelijk nog leeg terwijl iemand aan het kiezen is. */
export interface EigenPeriode {
  van: string;
  tot: string;
}

export const EIGEN = "eigen";

/**
 * De ISO-grenzen van een periode. "alles" laat ze allebei leeg, zodat de bron alles teruggeeft.
 *
 * <p>Een eigen periode geeft terug wat er is ingevuld: alleen een van-datum is een geldige vraag
 * ("vanaf 1 juli"), net als alleen een tot-datum. Pas als er niets staat is het hetzelfde als alles.
 */
export function periodeBereik(
  periode: Periode,
  vandaag = new Date(),
  eigen?: EigenPeriode,
): { from: string; to: string } {
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const jaar = vandaag.getFullYear();
  const maand = vandaag.getMonth();
  if (periode === "maand") {
    return { from: iso(new Date(Date.UTC(jaar, maand, 1))), to: iso(new Date(Date.UTC(jaar, maand + 1, 0))) };
  }
  if (periode === "kwartaal") {
    const start = Math.floor(maand / 3) * 3;
    return { from: iso(new Date(Date.UTC(jaar, start, 1))), to: iso(new Date(Date.UTC(jaar, start + 3, 0))) };
  }
  if (periode === "jaar") {
    return { from: iso(new Date(Date.UTC(jaar, 0, 1))), to: iso(new Date(Date.UTC(jaar, 11, 31))) };
  }
  if (periode === EIGEN) {
    return { from: eigen?.van ?? "", to: eigen?.tot ?? "" };
  }
  return { from: "", to: "" };
}

/** De keuze plus, bij een eigen periode, de twee datumvelden. */
export function PeriodeFilter({
  waarde,
  onWaarde,
  eigen,
  onEigen,
}: {
  waarde: Periode;
  onWaarde: (p: Periode) => void;
  eigen: EigenPeriode;
  onEigen: (e: EigenPeriode) => void;
}) {
  const t = useTranslate();
  return (
    <>
      <SelectField
        label={t("financieel.filter.periode")}
        value={waarde}
        onChange={(e) => onWaarde(e.target.value)}
        options={[
          { value: "maand", label: t("financieel.periode.maand") },
          { value: "kwartaal", label: t("financieel.periode.kwartaal") },
          { value: "jaar", label: t("financieel.periode.jaar") },
          { value: EIGEN, label: t("financieel.periode.eigen") },
          { value: "alles", label: t("financieel.periode.alles") },
        ]}
      />
      {waarde === EIGEN && (
        <>
          <DateField
            label={t("financieel.periode.van")}
            value={eigen.van}
            // De tot-datum begrenst de van-datum en andersom: een periode die achteruit loopt levert
            // altijd een lege lijst op, en dat ziet eruit als een scherm dat kapot is.
            max={eigen.tot || undefined}
            onChange={(e) => onEigen({ ...eigen, van: e.target.value })}
          />
          <DateField
            label={t("financieel.periode.tot")}
            value={eigen.tot}
            min={eigen.van || undefined}
            onChange={(e) => onEigen({ ...eigen, tot: e.target.value })}
          />
        </>
      )}
    </>
  );
}

/** De staat die bij {@link PeriodeFilter} hoort, zodat elk scherm hem op dezelfde manier bewaart. */
export function usePeriode(start = "maand") {
  const [periode, setPeriode] = useState<Periode>(start);
  const [eigen, setEigen] = useState<EigenPeriode>({ van: "", tot: "" });
  return { periode, setPeriode, eigen, setEigen };
}
